"use client"

import { useCallback, useEffect, useState, useMemo, useRef } from "react"
import ExitToAppIcon from "@mui/icons-material/ExitToApp"
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline"
import CakeIcon from "@mui/icons-material/Cake"
import axios from "axios"
import useAuthStore from "../../../Zustand/Store/useAuthStore"
import usePermissionDataStore from "../../../Zustand/Store/usePermissionDataStore"
import NotAllowed from "../../../Exceptions/NotAllowed"
import toast from "react-hot-toast"
import PeopleIcon from "@mui/icons-material/People"
import { MAIN_URL, getApiHeaders } from "../../../Configurations/Urls"
import { buildDatagridKey } from "../../../Configurations/datagridKey"
import { useNavigate, useLocation, useParams, useSearchParams } from "react-router-dom"
import TableDataGeneric from "../../../Configurations/TableDataGeneric"
import Layout4 from "../../DataLayouts/Layout4"
import { operatorRequiresValue } from '../../../Components/filters/operators';
import { fetchOrganizationDepartment } from "../../../Apis/Department-api"
import { fetchOrganizationDesignation } from "../../../Apis/Designation-api"
import { fetchWorkShift } from "../../../Apis/Workshift-api"
import {
  asOptionList,
  mergeToolbarFilters,
  ToolbarFilterSelect,
  useToolbarFilterChange,
} from "../../../Components/Table/toolbarFilters"

// Column fields must match backend response keys (department_name, designation_name, etc.)
const DEFAULT_COLUMNS = [
  {
    field: "employee_code",
    label: "Emp Code",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "name",
    label: "Name",
    visible: true,
    width: 200,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "department_name",
    label: "Department",
    visible: true,
    width: 180,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "designation_name",
    label: "Designation",
    visible: true,
    width: 180,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
]

const BANK_COLUMNS = [
  {
    field: "bank_name",
    label: "Bank Name",
    visible: false,
    width: 160,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "ifsc_code",
    label: "IFSC Code",
    visible: false,
    width: 140,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "upi_id",
    label: "UPI ID",
    visible: false,
    width: 160,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "account_number",
    label: "Account Number",
    visible: false,
    width: 160,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "account_type",
    label: "Account Type",
    visible: false,
    width: 140,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "qr_code_url",
    label: "QR Code URL",
    visible: false,
    width: 180,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
]

const mergeBankColumns = (columns = []) => {
  const existing = new Set(
    columns.map((c) => c.field || c.key).filter(Boolean)
  )
  const missing = BANK_COLUMNS.filter((col) => !existing.has(col.field)).map(
    (col) => ({
      ...col,
      key: col.field,
    })
  )
  return [...columns, ...missing]
}

// Backward compatibility: map legacy/saved column keys to canonical keys sent to API
const SORT_FIELD_ALIASES = {
  department: "department_name",
  designation: "designation_name",
  location: "location_name",
}

const EMPLOYEE_TOOLBAR_FILTERS = [
  { urlKey: "department_id", field: "department" },
  { urlKey: "designation_id", field: "designation" },
  { urlKey: "workshift_id", field: "work_shift" },
]

function EmployeeList({ mode }) {
  const { userData } = useAuthStore()
  const org = userData?.organization
  const { Permission } = usePermissionDataStore()
  const [showNotAllowed, setShowNotAllowed] = useState(false)

  const hasEditPermission = useMemo(() => {
    return (
      Permission &&
      Array.isArray(Permission) &&
      Permission.includes("EMPLOYEE_EDIT")
    )
  }, [Permission])

  const hasDeletePermission = useMemo(() => {
    return (
      Permission &&
      Array.isArray(Permission) &&
      Permission.includes("EMPLOYEE_DELETE")
    )
  }, [Permission])

  const [employee, setemployee] = useState([])
  const [loading, setLoading] = useState(true)
  const location = useLocation()
  const { id } = useParams()
  const fromDashboard = location.state?.fromDashboard || false
  const [searchParams, setSearchParams] = useSearchParams()

  const [paginationData, setPaginationData] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  })

  const [tableConfig, setTableConfig] = useState(null)
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS)
  const [loadingConfig, setLoadingConfig] = useState(true)
  const [departments, setDepartments] = useState([])
  const [designations, setDesignations] = useState([])
  const [workshifts, setWorkshifts] = useState([])

  const fetchControllerRef = useRef(null)
  const hasInitializedURL = useRef(false)
  const handleToolbarFilterChange = useToolbarFilterChange(setSearchParams)
 

  // Initialize URL parameters with defaults on mount
  useEffect(() => {
    if (hasInitializedURL.current) return

    const currentParams = new URLSearchParams(searchParams)
    let needsUpdate = false

    if (!currentParams.has("page")) {
      currentParams.set("page", "1")
      needsUpdate = true
    }

    if (!currentParams.has("pageSize")) {
      currentParams.set("pageSize", "10")
      needsUpdate = true
    }

    if (!currentParams.has("sortBy")) {
      currentParams.set("sortBy", "employee_code")
      needsUpdate = true
    }

    if (!currentParams.has("sortOrder")) {
      currentParams.set("sortOrder", "desc")
      needsUpdate = true
    }

    if (needsUpdate) {
      setSearchParams(currentParams, { replace: true })
    }

    hasInitializedURL.current = true
  }, [])

  useEffect(() => {
    if (!org?.organization_id) return
    let cancelled = false
    const loadOptions = async () => {
      try {
        const [deptRes, desigRes, shiftRes] = await Promise.allSettled([
          fetchOrganizationDepartment(org.organization_id),
          fetchOrganizationDesignation(org.organization_id),
          fetchWorkShift(org.organization_id),
        ])
        if (cancelled) return
        if (deptRes.status === "fulfilled") {
          setDepartments(asOptionList(deptRes.value, ["Departments", "data"]))
        }
        if (desigRes.status === "fulfilled") {
          setDesignations(
            asOptionList(desigRes.value, ["organizationdesignation", "data"])
          )
        }
        if (shiftRes.status === "fulfilled") {
          setWorkshifts(asOptionList(shiftRes.value, ["workshifts", "data"]))
        }
      } catch (error) {
        console.error("Failed to load employee toolbar filters", error)
      }
    }
    loadOptions()
    return () => {
      cancelled = true
    }
  }, [org?.organization_id])

  // // Build query params from URL
  // const buildQueryParamsFromURL = useMemo(() => {
  //   const params = {}
  //   params.page = Number.parseInt(searchParams.get("page"), 10) || 1
  //   params.pageSize = Number.parseInt(searchParams.get("pageSize"), 10) || 10

  //   const search = searchParams.get("search")
  //   if (search) params.search = search

  //   const sortBy = searchParams.get("sortBy")
  //   const sortOrder = searchParams.get("sortOrder")
  //   if (sortBy) {
  //     params.sortBy = sortBy
  //     params.sortOrder = sortOrder || "asc"
  //   }

  //   searchParams.forEach((value, key) => {
  //     if (key.startsWith("filter_") && value) {
  //       const filterKey = key.replace("filter_", "")
  //       params[filterKey] = value
  //     }
  //   })

  //   return params
  // }, [searchParams])

  // Fetch employees with filters - FIXED
  const fetchEmployeesWithFilters = useCallback(async () => {
    if (!org?.organization_id) return

    if (fetchControllerRef.current) {
      fetchControllerRef.current.abort()
    }

    fetchControllerRef.current = new AbortController()

    setLoading(true)
    try {
      // Build filters array from URL params
      const filters = []
      const processedFields = new Set() // Track processed fields to avoid duplicates
      let filterIndex = 0 // Track filter index
     
      searchParams.forEach((value, key) => {
        // Skip operator and logic params - they'll be processed with their field
        if (key.endsWith("_operator") || key.endsWith("_logic")) return
       
        if (key.startsWith("filter_")) {
          const field = key.replace("filter_", "")
         
          // Skip if already processed
          if (processedFields.has(field)) return
          processedFields.add(field)
         
          // Get operator from URL (e.g., filter_employee_code_operator=contains)
          const operatorKey = `filter_${field}_operator`
          const operator = searchParams.get(operatorKey) || 'contains' // Default to contains
         
          // Check if operator requires a value
          const requiresValue = operatorRequiresValue(operator)
         
          // Include filter if:
          // 1. Operator doesn't require value (today, this_week, this_month, is_null, is_not_null)
          // 2. OR value exists
          if (!requiresValue || value) {
            // For operators that don't require values, set value to null
            let filterValue = requiresValue ? value : null
           
            // Convert comma-separated string to array for operators that need arrays
            if (requiresValue && filterValue) {
              if (operator === 'equals' || operator === 'not_equals') {
                // Split by comma and trim whitespace, then convert to lowercase
                filterValue = filterValue.split(',').map(v => v.trim().toLowerCase())
              } else if (operator === 'between' || operator === 'not_between') {
                // Split by comma and trim whitespace for range operators
                filterValue = filterValue.split(',').map(v => v.trim())
              }
            }
           
            filters.push({
              field: field,
              operator: operator,
              value: filterValue
            })
           
            filterIndex++ // Increment index for next filter
          }
        }
      })

      const requestFilters = mergeToolbarFilters(
        filters,
        searchParams,
        EMPLOYEE_TOOLBAR_FILTERS
      )

      // Global search: send as 'search' so backend metadata engine searches all filterable fields
      const search = searchParams.get("search")

      const requestBody = {
        filters: requestFilters,
        page: Number.parseInt(searchParams.get("page"), 10) || 1,
        pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
        sort: {
          field: searchParams.get("sortBy") || "employee_code",
          direction: searchParams.get("sortOrder") || "desc"
        },
        ...(search && search.trim() !== "" && { search: search.trim() })
      }

      const filterUrl = `${MAIN_URL}/api/organizations/${org.organization_id}/employee/filters`

      const response = await fetch(
        filterUrl,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
          signal: fetchControllerRef.current.signal,
        },
      )

      const result = await response.json().catch(() => ({}))

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      // Debug logging
      console.log('Employee list API response:', result)
      console.log('Success:', result.success)
      console.log('Data length:', result.data?.length)
      console.log('Error:', result.error)
      console.log('Message:', result.message)

      if (result.success && result.data) {
        // Normalize profile_image_url to always be a full, usable URL for card view
        const normalizedEmployees = result.data.map((emp) => {
          const raw = emp?.profile_image_url

          if (!raw || typeof raw !== "string") {
            return { ...emp, profile_image_url: "" }
          }

          // If backend already sends full URL (http/https), keep as-is
          if (raw.startsWith("http://") || raw.startsWith("https://")) {
            return { ...emp, profile_image_url: raw }
          }

          // If raw already includes its folder (employees/, persons/, interns/),
          // prepend the storage base only once
          if (
            raw.startsWith("employees/") ||
            raw.startsWith("persons/") ||
            raw.startsWith("interns/")
          ) {
            return {
              ...emp,
              profile_image_url: `${MAIN_URL}/storage/app/public/${raw}`,
            }
          }

          // Backward compat: treat as a plain filename in employees folder
          return {
            ...emp,
            profile_image_url: `${MAIN_URL}/storage/app/public/employees/${raw}`,
          }
        })

        setemployee(normalizedEmployees)

        if (result.pagination) {
          // Backend may return 'limit' instead of 'pageSize'
          const pageSize = result.pagination.pageSize || result.pagination.limit || 10;
          const requestedPage = Number.parseInt(searchParams.get("page"), 10) || 1;
         
          setPaginationData({
            page: result.pagination.page || requestedPage, // Use requested page if backend doesn't return it
            pageSize: pageSize,
            total: result.pagination.total || 0,
            totalPages: result.pagination.totalPages || 1,
            hasNext: result.pagination.hasNext || false,
            hasPrev: result.pagination.hasPrev || false,
          })
        }
      } else {
        console.warn('API returned unsuccessful response:', result)
        setemployee([])
        setPaginationData({
          page: 1,
          pageSize: 10,
          total: 0,
          totalPages: 1,
          hasNext: false,
          hasPrev: false,
        })
        // Show error message to user
        if (result.message || result.error) {
          toast.error(result.message || result.error || 'Failed to fetch employees')
        }
      }
    } catch (err) {
      if (err.name === "AbortError") {
        console.log("Fetch aborted")
        return
      }
      console.error("Error fetching employees:", err)
      toast.error("Failed to fetch employees")
      setemployee([])
      setPaginationData({
        page: 1,
        pageSize: 10,
        total: 0,
        totalPages: 1,
        hasNext: false,
        hasPrev: false,
      })
    } finally {
      setLoading(false)
    }
  }, [org?.organization_id, searchParams])

  // Prefetch column catalog for configss fallback only.
  // Runtime columns come from TableDataGeneric get-by-context (org → general).
  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false)
        return
      }

      try {
        const datagridKey = buildDatagridKey("Employees", org.organization_id)
        const contextRes = await fetch(`${MAIN_URL}/api/datagrid/get-by-context`, {
          method: "POST",
          headers: getApiHeaders(),
          body: JSON.stringify({
            datagrid_key: datagridKey,
            organization_id: org.organization_id,
          }),
        })

        if (contextRes.ok) {
          const contextData = await contextRes.json()
          const serverCfg =
            contextData?.datagrid?.datagrid_default_configuration || null

          if (Array.isArray(serverCfg?.columns) && serverCfg.columns.length > 0) {
            const processedColumns = serverCfg.columns
              .map((col) => {
                const label = typeof col.label === "string" ? col.label.trim().toLowerCase() : ""
                if (label === "name" || col.field === "Name") {
                  return { ...col, field: "name", key: "name", label: "Name" }
                }
                const field = col.field && String(col.field).toLowerCase()
                if (field === "department") {
                  return { ...col, field: "department_name", key: "department_name", label: col.label || "Department" }
                }
                if (field === "designation") {
                  return { ...col, field: "designation_name", key: "designation_name", label: col.label || "Designation" }
                }
                if (field === "location") {
                  return { ...col, field: "location_name", key: "location_name", label: col.label || "Location" }
                }
                return {
                  ...col,
                  field: col.field || col.key,
                  key: col.key || col.field,
                }
              })
              .filter((col, idx, arr) => col.field !== "name" || arr.findIndex((c) => c.field === "name") === idx)
              .filter((col) => !["first_name", "middle_name", "last_name"].includes(col.field))

            const hasEmployeeName = processedColumns.some((c) => c.field === "name")
            const finalColumns = mergeBankColumns(
              hasEmployeeName
                ? processedColumns
                : [
                    {
                      field: "name",
                      key: "name",
                      label: "Name",
                      visible: true,
                      width: 200,
                      filterable: true,
                      sortable: true,
                      pinned: "none",
                      required: false,
                    },
                    ...processedColumns,
                  ]
            )

            const nextCfg = { ...serverCfg, columns: finalColumns }
            setTableConfig(nextCfg)
            setConfigColumns(finalColumns)
          } else {
            const defaults = mergeBankColumns(DEFAULT_COLUMNS)
            setTableConfig({ columns: defaults })
            setConfigColumns(defaults)
          }
        } else {
          setConfigColumns(mergeBankColumns(DEFAULT_COLUMNS))
        }
      } catch (error) {
        console.error("Error loading table configuration:", error)
        setConfigColumns(mergeBankColumns(DEFAULT_COLUMNS))
      } finally {
        setLoadingConfig(false)
      }
    }

    loadTableConfiguration()
  }, [org?.organization_id])

  // Fetch employees when URL params change (do not wait on column config)
  useEffect(() => {
    if (org?.organization_id && hasInitializedURL.current) {
      fetchEmployeesWithFilters()
    }

    return () => {
      if (fetchControllerRef.current) {
        fetchControllerRef.current.abort()
      }
    }
  }, [org?.organization_id, searchParams, fetchEmployeesWithFilters])

  const deleteemployee = useCallback(
    async (id) => {
      try {
        const org_id = org.organization_id
        await axios.delete(`${MAIN_URL}/api/organizations/${org_id}/employee/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        })
      } catch (error) {
        if (error.response && error.response.status === 401) {
          window.location.href = "/login"
          toast.error("Session Expired!")
        }
      }
    },
    [org],
  )

  const navigate = useNavigate()

  const onclickRow = useCallback(
    (item) => {
      // Ensure we have the correct organization_person_id
      const orgPersonId = item?.organization_person_id || item?.id
      if (!orgPersonId) {
        console.error('Missing organization_person_id in row data:', item)
        toast.error('Unable to open employee profile: Missing employee ID')
        return
      }
      const params = new URLSearchParams(searchParams)
      navigate(`/organization/employee/employee-details/employee-profile?id=${orgPersonId}&${params.toString()}`)
    },
    [navigate, searchParams],
  )

  // Handle sort change - update URL parameters; map legacy column keys for backward compatibility
  const handleSortChange = useCallback(
    (sortField, sortDirection) => {
      const raw = (sortField && String(sortField).trim()) || ""
      const normalizedField = raw.toLowerCase()
      const canonicalField = SORT_FIELD_ALIASES[normalizedField] ?? (normalizedField || raw)

      const params = new URLSearchParams(searchParams)
      params.set("sortBy", canonicalField)
      params.set("sortOrder", sortDirection)
      params.set("page", "1") // Reset to first page on sort
      setSearchParams(params, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  const handleDelete = useCallback(
    async (id) => {
      if (!hasDeletePermission) {
        setShowNotAllowed(true)
        return Promise.reject(new Error("Not allowed"))
      }
      try {
        const response = await fetch(`${MAIN_URL}/api/organizations/${org?.organization_id}/employee/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        toast.success("Employee deleted successfully")

        return Promise.resolve()
      } catch (error) {
        toast.error("Failed to delete employee")
        return Promise.reject(error)
      }
    },
    [org?.organization_id, hasDeletePermission],
  )

  const handleEdit = useCallback(
    (item) => {
      const employeeId = item?.organization_person_id || item?.employee_id || item?.id;
      if (!employeeId) {
        console.error('Employee ID is missing:', item);
        toast.error('Unable to edit: Employee ID is missing');
        return;
      }
      navigate(`/organization/employee/employee-details/edit/${employeeId}`)
    },
    [navigate],
  )

  const carddata = useMemo(
    () => [
      { key: "profile_image_url", type: "photo" },
      { key: "name", type: "String" },
      { key: "designation_name", type: "String" },
    ],
    [],
  )

  const icons = useMemo(
    () => [
      {
        key: "exitToAppIcon",
        element: <ExitToAppIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
      },
      {
        key: "viewHeadlineIcon",
        element: <ViewHeadlineIcon color="primary" />,
      },
      {
        key: "cakeIcon",
        element: <CakeIcon sx={{ color: "red" }} />,
      },
      {
        key: "peopleIcon",
        element: <PeopleIcon sx={{ color: "primary" }} />,
      },
    ],
    [],
  )

  const messages = useMemo(
    () => [
      "Employee details",
      "Employee details",
      "Add Employee",
      "Employee, Deleting This Employee will delete its Entire data from Employees Record of this Organization Permanently",
    ],
    [],
  )


       const handleShow = useCallback(
    (item) => {
      navigate(`/organization/employee/employee-details/view/${item.organization_person_id}`)
    },
    [navigate],
  )

  const toolbarExtras = (
    <>
      <ToolbarFilterSelect
        label="Department"
        urlKey="department_id"
        allLabel="All departments"
        options={departments}
        valueKey="organization_department_id"
        labelKey="department_name"
        searchParams={searchParams}
        onChange={handleToolbarFilterChange}
      />
      <ToolbarFilterSelect
        label="Designation"
        urlKey="designation_id"
        allLabel="All designations"
        options={designations}
        valueKey="organization_designation_id"
        labelKey="designation_name"
        searchParams={searchParams}
        onChange={handleToolbarFilterChange}
      />
      <ToolbarFilterSelect
        label="Workshift"
        urlKey="workshift_id"
        allLabel="All workshifts"
        options={workshifts}
        valueKey="organization_work_shift_id"
        labelKey="work_shift_name"
        searchParams={searchParams}
        onChange={handleToolbarFilterChange}
      />
    </>
  )

  const listGridConfig = useMemo(() => {
    const defaultVisibleColumns = DEFAULT_COLUMNS.filter((c) => c.visible).map(
      (c) => c.field
    )
    const mandatoryColumns = DEFAULT_COLUMNS.filter((c) => c.required).map(
      (c) => c.field
    )
    return {
      ...(tableConfig || {}),
      defaultVisibleColumns,
      mandatoryColumns,
    }
  }, [tableConfig])

  if (showNotAllowed) {
    return <NotAllowed />
  }

  return (
    <>
      <Layout4
        loading={loading}
        add_action={"EMPLOYEE_ADD"}
        delete_action={"EMPLOYEE_DELETE"}
        heading={
          <div style={{ cursor: "pointer" }} onClick={() => navigate(-1)}>
            Employees
          </div>
        }
        btnName={"Add Employee"}
        Data={employee}
        Icons={icons}
        messages={messages}
        Route={"/organization/employee/employee-details"}
        setData={setemployee}
        DeleteFunc={deleteemployee}
      />
      {!loadingConfig && (
        <>
          {fromDashboard ? (
            <TableDataGeneric
              tableName="Employees"
              primaryKey="organization_person_id"
              heading="Employees"
              data={employee}
              sortname={"name"}
              showActions={false}
              CardData={carddata}
              Route="/organization/employee/employee-details"
              DeleteFunc={hasDeletePermission ? handleDelete : undefined}
              EditFunc={hasEditPermission ? handleEdit : undefined}
              handleShow={handleShow}
              token={localStorage.getItem("token")}
              configss={configColumns}
              onclickRow={onclickRow}
              linkType={"Name"}
              paginationData={paginationData}
              onSortChange={handleSortChange}
              filterModule="employees"
              edit_delete_action={["EMPLOYEE_EDIT", "EMPLOYEE_DELETE"]}
              config={listGridConfig}
              toolbarExtras={toolbarExtras}
            />
          ) : (
            <TableDataGeneric
              tableName="Employees"
              primaryKey={"organization_person_id"}
              recordKey={"employee_code"}
              recordLabel={"Employee Code"}
              heading="Employees"
              data={employee}
              sortname={"name"}
              showActions={true}
              CardData={carddata}
              Route="/organization/employee/employee-details"
              DeleteFunc={hasDeletePermission ? handleDelete : undefined}
              EditFunc={hasEditPermission ? handleEdit : undefined}
              handleShow={handleShow}
              onclickRow={onclickRow}
              token={localStorage.getItem("token")}
              linkType={"name"}
              configss={configColumns}
              paginationData={paginationData}
              onSortChange={handleSortChange}
              filterModule="employees"
              edit_delete_action={["EMPLOYEE_EDIT", "EMPLOYEE_DELETE"]}
              config={listGridConfig}
              toolbarExtras={toolbarExtras}
            />
          )}
        </>
      )}
    </>
  )
}

export default EmployeeList
